import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import LocationTracker from "./LocationTracker";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { ShieldAlert } from "lucide-react";

export default function DashboardLayout() {
    const [open, setOpen] = useState(false);
    const [expired, setExpired] = useState(false);
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const sendHeartbeat = async () => {
            try {
                await api.post("/auth/heartbeat");
            } catch (err) {}
        };
        sendHeartbeat();
        const interval = setInterval(sendHeartbeat, 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!user) return;
        const checkBilling = async () => {
            try {
                const res = await api.get("/billing/status");
                if (res.data.plan === "free" && res.data.trial_days_left <= 0) {
                    setExpired(true);
                } else {
                    setExpired(false);
                }
            } catch (err) {
                console.error("Failed to check billing status:", err);
            }
        };
        checkBilling();
    }, [user, location.pathname]);

    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            <LocationTracker />
            {/* ambient blobs */}
            <div
                aria-hidden
                className="pointer-events-none fixed -top-40 -left-40 w-[420px] h-[420px] rounded-full bg-primary/20 dark:bg-primary/15 blur-3xl animate-blob-float"
            />
            <div
                aria-hidden
                className="pointer-events-none fixed bottom-0 right-0 w-[480px] h-[480px] rounded-full bg-accent/20 dark:bg-accent/10 blur-3xl animate-blob-float"
                style={{ animationDelay: "6s" }}
            />

            <Sidebar open={open} onClose={() => setOpen(false)} />

            <div className="lg:pl-72 relative">
                <Topbar onMenuClick={() => setOpen(true)} />
                <main className="px-4 lg:px-8 py-6 lg:py-10 max-w-[1600px] mx-auto" data-testid="main-content">
                    <Outlet />
                </main>
            </div>

            {expired && location.pathname !== "/billing" && (
                <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-[9999] flex items-center justify-center p-6">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 max-w-md w-full shadow-2xl text-center space-y-6">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-950/20 text-red-500 rounded-full flex items-center justify-center mx-auto animate-bounce">
                            <ShieldAlert className="w-8 h-8" />
                        </div>
                        <div className="space-y-2">
                            <h2 className="font-heading text-2xl font-bold text-slate-800 dark:text-slate-100">14-Day Free Trial Expired</h2>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                Your workspace free trial has expired. To resume using ParshHR's advanced dashboards, attendance tracking, and document services, please upgrade your subscription.
                            </p>
                        </div>
                        
                        {(user?.role === "super_admin" || user?.role === "hr") ? (
                            <button
                                onClick={() => navigate("/billing")}
                                className="w-full py-3 px-6 bg-primary text-white rounded-full text-xs font-bold shadow-lg shadow-primary/30 hover:opacity-90 active:scale-95 transition-all"
                            >
                                Go to Billing Dashboard
                            </button>
                        ) : (
                            <div className="text-xs font-semibold text-destructive bg-destructive/10 rounded-xl p-3">
                                Please contact your workspace administrator to renew the plan subscription.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
